/**
 * SVU Exam Portal — Date Utilities (IST-safe)
 *
 * Root problem:
 *   Without dateStrings:true — mysql2 converts DATE "2026-04-09" into a JS
 *   Date object at IST midnight → serializes as "2026-04-08T18:30:00.000Z"
 *   (IST 00:00 = UTC -5:30 = prev day 18:30). Slicing [:10] gives "2026-04-08".
 *
 * Primary fix: dateStrings:true in db.js.
 *   mysql2 now returns DATE columns as plain strings "2026-04-09".
 *
 * Fallback fix (here): If an ISO datetime string is received, shift by +5:30
 *   before extracting the date so it reflects IST — not UTC.
 */

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000; // UTC+5:30

/**
 * Parse any date value from the DB and return a local Date object.
 * Handles:
 *   "2026-04-09"                  → plain date string (from dateStrings:true)
 *   "2026-04-08T18:30:00.000Z"    → ISO datetime (legacy fallback)
 *   "2026-04-09 00:00:00"         → MySQL datetime string
 *   Date objects
 * @param {string|Date} val
 * @returns {Date|null}
 */
export const parseLocalDate = (val) => {
    if (!val) return null;

    const str = (val instanceof Date) ? val.toISOString() : String(val).trim();

    // ── Case 1: Plain date "YYYY-MM-DD" (what we get with dateStrings:true) ──────
    if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
        const [y, m, d] = str.split('-').map(Number);
        return new Date(y, m - 1, d); // local midnight — no UTC shift
    }

    // ── Case 2: MySQL datetime string "YYYY-MM-DD HH:MM:SS" ────────────────────
    const mysqlMatch = str.match(/^(\d{4}-\d{2}-\d{2})\s/);
    if (mysqlMatch) {
        const [y, m, d] = mysqlMatch[1].split('-').map(Number);
        return new Date(y, m - 1, d);
    }

    // ── Case 3: ISO datetime with Z / offset — convert to IST then extract date ─
    try {
        const utcMs = new Date(str).getTime();
        if (isNaN(utcMs)) return null;
        // Shift UTC time to IST by adding +5:30 offset
        const istDate = new Date(utcMs + IST_OFFSET_MS);
        return new Date(istDate.getUTCFullYear(), istDate.getUTCMonth(), istDate.getUTCDate());
    } catch {
        return null;
    }
};

/**
 * Format a DB date value as Indian date: "09 Apr 2026"
 * @param {string|Date} val
 * @returns {string}
 */
export const formatIndianDate = (val) => {
    const d = parseLocalDate(val);
    if (!d) return '—';
    return d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};

/**
 * Format a DB date value as short Indian date: "09 Apr"
 * @param {string|Date} val
 * @returns {string}
 */
export const formatIndianDateShort = (val) => {
    const d = parseLocalDate(val);
    if (!d) return '—';
    return d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
    });
};

/**
 * Format a DB date value as numeric: "09/04/2026"
 * @param {string|Date} val
 * @returns {string}
 */
export const formatIndianDateNumeric = (val) => {
    const d = parseLocalDate(val);
    if (!d) return '—';
    return d.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
};
